import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/navbar.tsx';
import Footer from './components/footer.tsx';
import ScrollToTop from './components/ScrollToTop.tsx';
import Home from './pages/home/home.tsx';
import Login from './pages/login.tsx';
import Register from './pages/register.tsx';
import ForgotPassword from './pages/forgot-password.tsx';
import ResetPassword from './pages/reset-password.tsx';
import OAuthCallback from './pages/oauth-callback.tsx';
import AdminRoute from './components/AdminRoute.tsx';
import PrivateRoute from './components/PrivateRoute.tsx';
import DashboardLayout from './layouts/DashboardLayout.tsx';

const VerifyEmailPage = lazy(() => import('./pages/verify-email.tsx'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard.tsx'));
const Profile = lazy(() => import('./pages/profile.tsx'));
const Plans = lazy(() => import('./pages/plans/Plans.tsx'));
const Features = lazy(() => import('./pages/features.tsx'));
const Pricing = lazy(() => import('./pages/pricing.tsx'));
const Checkout = lazy(() => import('./pages/checkout.tsx'));
const About = lazy(() => import('./pages/about.tsx'));
const Contact = lazy(() => import('./pages/contact.tsx'));
const Security = lazy(() => import('./pages/security.tsx'));
const FaqPage = lazy(() => import('./pages/faq.tsx'));
const MyPlanPage = lazy(() => import('./pages/my-plan/MyPlanPage.tsx'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage.tsx'));
const AgendaPool = lazy(() => import('./pages/agenda-pool/AgendaPool.tsx'));
const MeetingRoute = lazy(() => import('./pages/meeting/components/MeetingRoute.tsx'));
const RecordingsPage = lazy(() => import('./pages/recordings/RecordingsPage.tsx'));
const SummariesPage = lazy(() => import('./pages/summaries/SummariesPage.tsx'));
const WhiteboardsPage = lazy(() => import('./pages/whiteboards/WhiteboardsPage.tsx'));
const WhiteboardEditorPage = lazy(() => import('./pages/whiteboards/WhiteboardEditorPage.tsx'));
const NotesPage = lazy(() => import('./pages/notes/NotesPage.tsx'));
const NoteEditorPage = lazy(() => import('./pages/notes/NoteEditorPage.tsx'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage.tsx'));
const GoogleCalendarCallback = lazy(() => import('./pages/calendar/GoogleCalendarCallback.tsx'));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-gray-300 text-sm">
      Yükleniyor…
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();
  
  // Navbar gösterilmeyecek sayfalar
  const hideNavbar = location.pathname.startsWith('/meeting/') ||
                     location.pathname === '/login' ||
                     location.pathname === '/register' ||
                     location.pathname === '/forgot-password' ||
                     location.pathname.startsWith('/reset-password') ||
                     location.pathname.startsWith('/verify-email') ||
                     location.pathname === '/oauth/callback';

  // Footer gösterilmeyecek sayfalar
  const hideFooter = location.pathname.startsWith('/meeting/') ||
                     location.pathname === '/login' ||
                     location.pathname === '/register' ||
                     location.pathname === '/forgot-password' ||
                     location.pathname.startsWith('/reset-password') ||
                     location.pathname.startsWith('/verify-email') ||
                     location.pathname === '/oauth/callback';

  return (
    <div className="min-h-screen flex flex-col text-white">
      <ScrollToTop />
      {!hideNavbar && <Navbar />}
      
      <div className="flex-1">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public - Layout yok */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/checkout" element={
              <PrivateRoute>
                <Checkout />
              </PrivateRoute>
            } />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/security" element={<Security />} />

            {/* Private - Dashboard Layout */}
            <Route path="/admin" element={
              <AdminRoute>
                <DashboardLayout><AdminPage /></DashboardLayout>
              </AdminRoute>
            } />
            <Route path="/dashboard" element={
              <PrivateRoute>
                <DashboardLayout><Dashboard /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                <DashboardLayout><Profile /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/my-plan" element={
              <PrivateRoute>
                <DashboardLayout><MyPlanPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/plans" element={
              <PrivateRoute>
                <DashboardLayout><Plans /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/recordings" element={
              <PrivateRoute>
                <DashboardLayout><RecordingsPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/summaries" element={
              <PrivateRoute>
                <DashboardLayout><SummariesPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/agenda-pool" element={
              <PrivateRoute>
                <DashboardLayout><AgendaPool /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/calendar" element={
              <PrivateRoute>
                <DashboardLayout><CalendarPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/calendar/google/callback" element={
              <PrivateRoute>
                <GoogleCalendarCallback />
              </PrivateRoute>
            } />
            <Route path="/products/whiteboards" element={
              <PrivateRoute>
                <DashboardLayout><WhiteboardsPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/products/whiteboards/:id" element={
              <PrivateRoute>
                <DashboardLayout><WhiteboardEditorPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/products/notes" element={
              <PrivateRoute>
                <DashboardLayout><NotesPage /></DashboardLayout>
              </PrivateRoute>
            } />
            <Route path="/products/notes/:id" element={
              <PrivateRoute>
                <DashboardLayout><NoteEditorPage /></DashboardLayout>
              </PrivateRoute>
            } />

            {/* Meeting - Misafir veya Kayıtlı Kullanıcı */}
            <Route path="/meeting/:id" element={<MeetingRoute />} />
          </Routes>
        </Suspense>
      </div>

      {/* Footer - her zaman tam genişlik */}
      {!hideFooter && <Footer />}
    </div>
  );
}

export default App;
